import { Button, Dialog, DialogProps, DialogTitle, Paper, useMediaQuery, useTheme } from '@mui/material';
import { FileManagement, TestDataFrame, TestDataType, WorkflowNodeTestData, WorkflowNodeType } from 'api/model.ts';
import { useMemo, useState } from 'react';
import { getFilesOptions, getNodeConfig, NodeConfig } from 'api/workflows-steps/formSchema.tsx';
import { CustomFormat } from 'components/form/CustomFormats.ts';
import { useParams } from 'react-router';
import useFileManagement from 'api/file-management/useFileManagement';
import { HiddenConstantWidget } from 'components/form/HiddenConstantWidget.tsx';
import { UiSchema } from '@rjsf/utils';
import { JSONSchema7 } from 'json-schema';
import { FileManagementWidget } from 'components/form/FileManagementWidget';
import DataframeField from 'components/form/DataframeField';
import ToggleButtonWidget from 'components/form/ToggleButtonWidget';
import { Dataset, Delete, Description, LibraryAdd, Restore, Save } from '@mui/icons-material';
import CustomForm from 'components/form/CustomForm';
import { GridColType } from '@mui/x-data-grid';
import { CSV_FILE_TYPES, EXCEL_FILE_TYPES } from 'utils/helpers';
import ResizableTextarea from 'components/form/ResizableTextarea';

const initialTestData: WorkflowNodeTestData = {
  type: TestDataType.FILE,
  data: null,
};

const initialDataframe: TestDataFrame = {
  col1: [1, 2, 3],
  col2: ['a', 'b', 'c'],
};

const FORM_ID = 'test-data-form';

const getFormSchema = (nodeConfig?: NodeConfig, files?: FileManagement[]): JSONSchema7 => {
  const isInput = !nodeConfig?.target;
  const isOutput = !nodeConfig?.source;

  const csvFiles = getFilesOptions(files, CSV_FILE_TYPES);
  const xlsFiles = getFilesOptions(files, EXCEL_FILE_TYPES);
  const filesOptions = [...csvFiles, ...xlsFiles];

  const fileTypeDependencies = [];

  if (csvFiles.length > 0) {
    fileTypeDependencies.push({
      properties: {
        file_path: {
          anyOf: csvFiles,
        },
        delimiter: { type: ['string', 'null'], title: 'Delimiter' },
        file_type: { type: 'string', const: 'csv' },
      },
    });
  }

  if (xlsFiles.length > 0) {
    fileTypeDependencies.push({
      properties: {
        file_path: {
          anyOf: xlsFiles,
        },
        file_type: { type: 'string', const: 'xls' },
      },
    });
  }

  return {
    type: 'object',
    properties: {
      type: {
        type: 'string',
        anyOf: [
          { enum: [TestDataType.FILE], title: 'File' },
          { enum: [TestDataType.DATAFRAME], title: 'Dataframe' },
          { enum: [TestDataType.LATEST_RESULT], title: 'Latest Result' },
          { enum: [TestDataType.NEW_COLUMN], title: 'New Column' },
        ],
        title: 'Test data type',
        default: TestDataType.FILE,
      },
    },
    required: ['type'],
    dependencies: {
      type: {
        oneOf: [
          {
            properties: {
              type: {
                enum: [TestDataType.FILE],
              },
              data: {
                type: 'object',
                title: 'File data',
                properties: {
                  file_path: {
                    type: 'string',
                    title: 'File',
                    anyOf: filesOptions,
                  },
                },
                required: ['file_path'],
                dependencies: {
                  file_path: {
                    oneOf: [...fileTypeDependencies],
                  },
                },
              },
            },
            required: ['type'],
          },
          {
            properties: {
              type: {
                enum: [TestDataType.NEW_COLUMN],
              },
              column: {
                type: 'string',
                title: 'New column name',
                format: CustomFormat.ColumnName,
              },
              column_type: {
                type: 'string',
                title: 'Column type',
                enum: ['string', 'number', 'boolean'],
                default: 'string',
              },
            },
            dependencies: {
              column_type: {
                oneOf: [
                  {
                    properties: {
                      column_type: {
                        enum: ['string'],
                      },
                      data_string: {
                        type: ['string', 'null'],
                        title: 'Value (string)',
                      },
                    },
                    required: ['column_type'],
                  },
                  {
                    properties: {
                      column_type: {
                        enum: ['number'],
                      },
                      data_number: {
                        type: ['number', 'null'],
                        title: 'Value (number)',
                      },
                    },
                    required: ['column_type'],
                  },
                  {
                    properties: {
                      column_type: {
                        enum: ['boolean'],
                      },
                      data_boolean: {
                        enum: [true, false, null],
                        title: 'Value (boolean)',
                      },
                    },
                    required: ['column_type'],
                  },
                ],
              },
            },
            required: ['type', 'column', 'column_type'],
          },
          {
            properties: {
              type: {
                enum: [TestDataType.DATAFRAME],
              },
              data: {
                type: 'object',
                title: 'Dataframe',
                properties: {},
              },
              append: {
                type: 'boolean',
                title: 'Append to input dataframe?',
                description:
                  "When enabled, the created dataframe will be appended (column-wise) to the node's input data instead of replacing it.",
                default: false,
                readOnly: isInput || isOutput,
              },
            },
            required: ['type'],
          },
        ],
      },
    },
  } as JSONSchema7;
};

const getUiSchema = (type: TestDataType, nodeConfig?: NodeConfig) => {
  const isInput = !nodeConfig?.target;
  const isOutput = !nodeConfig?.source;
  const uiSchema: UiSchema = {
    type: {
      'ui:widget': ToggleButtonWidget,
      'ui:options': {
        icons: {
          [TestDataType.FILE]: <Description />,
          [TestDataType.DATAFRAME]: <Dataset />,
          [TestDataType.LATEST_RESULT]: <Restore />,
          [TestDataType.NEW_COLUMN]: <LibraryAdd />,
        },
        descriptions: {
          [TestDataType.FILE]: "Uses the selected file as the node's output",
          [TestDataType.DATAFRAME]: "Uses the created dataframe as the node's output",
          [TestDataType.LATEST_RESULT]: "Uses the latest completed node execution result as the node's output",
          [TestDataType.NEW_COLUMN]: "Uses the node input with an added column as the node's output",
        },
        disabledOptions: isInput || isOutput ? [TestDataType.NEW_COLUMN] : [],
      },
    },
  };

  if (type === TestDataType.FILE) {
    uiSchema.data = {
      file_path: {
        'ui:widget': FileManagementWidget,
        'ui:options': {
          accept: '.xls,.xlsx,.csv',
        },
      },
      file_type: {
        'ui:widget': HiddenConstantWidget,
      },
    };
  } else if (type === TestDataType.NEW_COLUMN) {
    uiSchema.data_string = {
      'ui:widget': ResizableTextarea,
    };
  } else if (type === TestDataType.DATAFRAME) {
    uiSchema.data = {
      'ui:field': DataframeField,
    };
  }

  return uiSchema;
};

const initializeColumnType = (data?: WorkflowNodeTestData & { column_type?: GridColType }) => {
  if (!data) return data;

  if (data.type === TestDataType.NEW_COLUMN) {
    const columnType = data.column_type ?? (data.data != null ? typeof data.data : 'string');
    const dataFieldKey = `data_${columnType}`;
    return { ...data, column_type: columnType, [dataFieldKey]: data.data };
  }
  return data;
};

type EditTestDataDialogProps = {
  open: boolean;
  onSubmit: (data?: WorkflowNodeTestData) => void;
  onClose: () => void;
  nodeType: WorkflowNodeType;
  initialData?: WorkflowNodeTestData;
  defaultColumn?: string;
} & DialogProps;

const EditTestDataDialog = ({
  open,
  onClose,
  onSubmit,
  initialData,
  nodeType,
  defaultColumn,
}: EditTestDataDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { projectId } = useParams();
  const { files } = useFileManagement(projectId, true);
  const nodeConfig = useMemo(() => getNodeConfig(nodeType), [nodeType]);
  const initialValue: WorkflowNodeTestData = defaultColumn
    ? { type: TestDataType.NEW_COLUMN, column: defaultColumn, data: null }
    : initialTestData;
  const [data, setData] = useState<WorkflowNodeTestData>(initializeColumnType(initialData) ?? initialValue);

  const handleOnChange = (formData: WorkflowNodeTestData) => {
    if (data.type !== formData.type) {
      formData.data = formData.type === TestDataType.DATAFRAME ? initialDataframe : null;
    }
    setData(formData);
  };

  const handleSubmit = (
    formData: WorkflowNodeTestData & {
      column_type?: GridColType;
      data_string?: string;
      data_number?: number;
      data_boolean?: boolean;
    },
  ) => {
    if (formData.type === TestDataType.NEW_COLUMN) {
      const { data_string, data_number, data_boolean, column_type, ...rest } = formData;
      const value = column_type === 'number' ? data_number : column_type === 'boolean' ? data_boolean : data_string;
      onSubmit({ ...rest, column_type, data: value ?? null } as WorkflowNodeTestData);
      return;
    }
    onSubmit(formData);
  };

  return (
    <Dialog fullScreen={fullScreen} maxWidth="lg" open={open} PaperComponent={Paper} onClose={onClose}>
      <div className="flex gap-2 flex-col m-10">
        <DialogTitle className="p-0">
          {nodeConfig?.source ? 'Edit node test data' : 'Edit expected output data'}
        </DialogTitle>
        <div className="w-full">
          <div>
            <CustomForm
              schema={getFormSchema(nodeConfig, files)}
              uiSchema={getUiSchema(data.type, nodeConfig)}
              onSubmit={handleSubmit}
              onChange={handleOnChange}
              initialValues={data}
              formId={FORM_ID}
              formContext={{ files }}
            />
            <div className="flex justify-end gap-2 mt-5">
              <Button color="secondary" variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button color="error" variant="outlined" onClick={() => onSubmit(undefined)} startIcon={<Delete />}>
                Remove
              </Button>
              <Button variant="contained" startIcon={<Save />} type="submit" form={FORM_ID}>
                Submit
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
export default EditTestDataDialog;
